import { Gift } from '@/components/ui/icons';
import { useEffect, useState } from 'react';
import { BLAZE_NAME, giftLength, type BlazeGiftPreviewDTO } from '@clanfire/shared';
import { errorMessage } from '@/api/client';
import { blazeApi } from '@/api/endpoints';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Spinner';
import { useBlaze } from '@/stores/blaze';
import { toast } from '@/stores/ui';

const GIFT_LINK_RE = /https?:\/\/([^\s/]+)\/gift\/([A-Za-z0-9-]{10,40})(?![A-Za-z0-9-])/g;

/** Blaze gift codes for this ClanFire instance found in a message (max 3). */
export function extractGiftCodes(content: string): string[] {
  const codes = new Set<string>();
  for (const match of content.matchAll(GIFT_LINK_RE)) {
    if (match[1] === location.host) codes.add(match[2]!);
    if (codes.size === 3) break;
  }
  return [...codes];
}

type State = { status: 'loading' } | { status: 'ready'; gift: BlazeGiftPreviewDTO } | { status: 'invalid' };

/** A Blaze gift link as a card: how long it is, who it's from, and claiming it right here. */
export function GiftEmbed({ code, fromMe }: { code: string; fromMe: boolean }) {
  const [state, setState] = useState<State>({ status: 'loading' });
  const [claiming, setClaiming] = useState(false);

  useEffect(() => {
    let cancelled = false;
    blazeApi
      .gift(code)
      .then((gift) => !cancelled && setState({ status: 'ready', gift }))
      .catch(() => !cancelled && setState({ status: 'invalid' }));
    return () => {
      cancelled = true;
    };
  }, [code]);

  const claim = async () => {
    setClaiming(true);
    try {
      useBlaze.getState().set(await blazeApi.redeem(code));
      setState((s) => (s.status === 'ready' ? { status: 'ready', gift: { ...s.gift, redeemable: false } } : s));
      toast.success(`Gift claimed. Enjoy ${BLAZE_NAME}!`);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setClaiming(false);
    }
  };

  const gift = state.status === 'ready' ? state.gift : null;
  return (
    <div className="mt-1.5 w-full max-w-[420px] rounded-xl border border-line bg-sidebar p-4" aria-label={`${BLAZE_NAME} gift`}>
      <p className="mb-3 text-xs font-bold tracking-wide text-fg-muted uppercase">{fromMe ? 'You sent a gift' : "You've been sent a gift"}</p>
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[30%] bg-gradient-to-br from-[#f97316] via-[#e11d48] to-[#7c3aed] text-white">
          <Gift size={26} aria-hidden />
        </div>
        {state.status === 'loading' ? (
          <div className="flex flex-1 flex-col gap-2">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-3 w-24" />
          </div>
        ) : !gift ? (
          <div className="min-w-0 flex-1">
            <p className="font-bold text-danger">This gift link doesn&apos;t work</p>
            <p className="text-sm text-fg-muted">It may have been refunded.</p>
          </div>
        ) : (
          <>
            <div className="min-w-0 flex-1">
              <p className="truncate font-bold text-fg">
                {giftLength(gift.months)} of {BLAZE_NAME}
              </p>
              {gift.from && !fromMe && (
                <p className="flex items-center gap-1.5 text-xs text-fg-muted">
                  <Avatar user={gift.from} size={16} /> From {gift.from.displayName}
                </p>
              )}
            </div>
            {fromMe ? (
              <span className="text-sm font-semibold text-fg-muted">{gift.redeemable ? 'Not claimed yet' : 'Claimed'}</span>
            ) : (
              <Button size="sm" disabled={!gift.redeemable} loading={claiming} onClick={() => void claim()}>
                {gift.redeemable ? 'Claim' : 'Claimed'}
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
